import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useAuth } from '../../app/useAuth'
import { Icon } from '../../components/icons'
import { PageHeader, Spinner } from '../../components/ui'
import { api } from '../../lib/api/client'
import './home.css'

type Stat = {
  label: string
  value: number
  note: string
  to: string
}

export function Home() {
  const { user, has } = useAuth()
  const isSiteAdmin = has('admin')
  // The classroom cards are not the site administrator's job, and the screens
  // they linked to are no longer in their navigation (app/Shell.tsx). Counting
  // every school's papers under "My papers" was never right either.
  const isTeacher = !isSiteAdmin && has('teacher')
  const isReviewer = !isSiteAdmin && has('reviewer')
  const isPrinter = !isSiteAdmin && has('print_admin', 'school_admin')
  const isSchoolAdmin = has('school_admin')

  const papers = useQuery({
    queryKey: ['worksheets'],
    queryFn: () => api.listWorksheets(false),
    enabled: isTeacher,
  })
  const review = useQuery({
    queryKey: ['queue', 'review'],
    queryFn: api.reviewQueue,
    enabled: isReviewer,
  })
  const printing = useQuery({
    queryKey: ['queue', 'printing'],
    queryFn: api.printQueue,
    enabled: isPrinter,
  })
  const people = useQuery({
    queryKey: ['people'],
    queryFn: () => api.people(),
    enabled: isSchoolAdmin,
  })
  const templates = useQuery({
    queryKey: ['templates'],
    queryFn: () => api.templates(),
    enabled: isSchoolAdmin,
  })
  const invitations = useQuery({
    queryKey: ['invitations'],
    queryFn: () => api.invitations(),
    enabled: isSchoolAdmin,
  })
  const schools = useQuery({
    queryKey: ['platform', 'schools'],
    queryFn: api.platformSchools,
    enabled: isSiteAdmin,
  })

  const loading = [papers, review, printing, people, templates, invitations, schools].some(
    (query) => query.isLoading && query.fetchStatus !== 'idle',
  )
  if (loading) return <Spinner label="Preparing your home page" />

  const stats: Stat[] = []

  if (isTeacher) {
    const rows = papers.data ?? []
    stats.push(
      { label: 'My papers', value: rows.length, note: 'All papers you created', to: '/papers' },
      {
        label: 'In progress',
        value: rows.filter((paper) =>
          ['DRAFT', 'CHANGES_REQUESTED', 'IN_REVIEW'].includes(paper.status),
        ).length,
        note: 'Draft, changes, or review',
        to: '/papers',
      },
      {
        label: 'Printed',
        value: rows.filter((paper) => paper.status === 'PRINTED').length,
        note: 'Completed papers',
        to: '/papers',
      },
    )
  }

  if (isReviewer) {
    const rows = review.data ?? []
    stats.push(
      { label: 'Awaiting review', value: rows.length, note: 'Papers ready to check', to: '/review' },
      {
        label: 'Open comments',
        value: rows.reduce((total, paper) => total + paper.open_comments, 0),
        note: 'Comments still unresolved',
        to: '/review',
      },
      {
        label: 'Questions',
        value: rows.reduce((total, paper) => total + paper.question_count, 0),
        note: 'Questions in the queue',
        to: '/review',
      },
    )
  }

  if (isPrinter) {
    const rows = printing.data ?? []
    stats.push(
      { label: 'Waiting to print', value: rows.length, note: 'Approved or with printing', to: '/printing' },
      {
        label: 'Copies requested',
        value: rows.reduce((total, paper) => total + (paper.print_run.copies ?? 0), 0),
        note: 'Across the current queue',
        to: '/printing',
      },
      {
        label: 'Missing copy count',
        value: rows.filter((paper) => paper.print_run.copies == null).length,
        note: 'Need clarification',
        to: '/printing',
      },
    )
  }

  if (isSchoolAdmin) {
    const schoolPeople = people.data ?? []
    stats.push(
      {
        label: 'Active people',
        value: schoolPeople.filter((person) => person.is_active).length,
        note: 'Current school accounts',
        to: '/settings',
      },
      {
        label: 'Paper headers',
        value: templates.data?.length ?? 0,
        note: 'Available templates',
        to: '/settings',
      },
      {
        label: 'Pending invites',
        value: invitations.data?.length ?? 0,
        note: 'Waiting to be accepted',
        to: '/settings',
      },
    )
  }

  // Which jobs nobody at this school can do. Counted from active people only:
  // an invitation that has not been accepted cannot review anything.
  const activePeople = (people.data ?? []).filter((person) => person.is_active)
  const missingRoles = isSchoolAdmin
    ? (['teacher', 'reviewer'] as const).filter(
        (role) => !activePeople.some((person) => person.roles.includes(role)),
      )
    : []
  const schoolName = user?.school_name ?? 'your school'

  if (isSiteAdmin) {
    // Exclude the internal platform workspace the site admin belongs to,
    // the same way Schools.tsx does, so the count reflects real schools only.
    const rows = (schools.data ?? []).filter((school) => school.id !== user?.school_id)
    stats.unshift(
      {
        label: 'Schools',
        value: rows.length,
        note: 'Schools across the platform',
        to: '/schools',
      },
      {
        label: 'People invited',
        value: rows.reduce((total, school) => total + school.pending_invitations, 0),
        note: 'Pending school invitations',
        to: '/schools',
      },
      {
        label: 'Role gaps',
        value: rows.filter(
          (school) =>
            !school.teacher_count ||
            !school.reviewer_count ||
            !school.administrator_count,
        ).length,
        note: 'Schools missing a required role',
        to: '/schools',
      },
    )
  }

  return (
    <div className="tc-home">
      <PageHeader
        eyebrow={roleMessage(user?.roles ?? [])}
        title={`${greeting()}, ${user?.first_name}.`}
        icon={<Icon.home size={20} />}
        description={welcomeMessage(user?.roles ?? [])}
        actions={<HomePrimaryAction isSiteAdmin={isSiteAdmin} isSchoolAdmin={isSchoolAdmin} isTeacher={isTeacher} isReviewer={isReviewer} isPrinter={isPrinter} />}
      />

      {isSchoolAdmin && missingRoles.length > 0 && (
        // The first screen a new school administrator ever sees, and until this
        // it said nothing. A school with no teacher cannot make a paper and a
        // school with no reviewer cannot approve one - but the person who finds
        // that out is a teacher, later, hitting a refusal. The site
        // administrator was already shown this as "role gaps"; the person who
        // can actually fix it was not.
        //
        // Labelled rather than role="status": this is static page content, not
        // something that changed, and a live region announces it over the
        // heading on every single load.
        <section className="tc-home-gap" aria-labelledby="home-gap-title">
          <Icon.warning size={18} />
          <div>
            <strong id="home-gap-title">
              {missingRoles.length === 1
                ? `Nobody at ${schoolName} can ${ROLE_GAP[missingRoles[0]].verb} yet.`
                : `${schoolName} is not ready to make papers yet.`}
            </strong>
            <p>
              {listRoles(missingRoles.map((role) => ROLE_GAP[role].noun))} still to invite.
              Papers cannot get through without {missingRoles.length === 1 ? 'one' : 'them'}.
            </p>
          </div>
          <Link className="tc-btn tc-btn-primary" to="/settings?tab=people">
            Invite a colleague
          </Link>
        </section>
      )}

      <section aria-labelledby="overview-title">
        <h2 id="overview-title">Today at a glance</h2>
        <div className="tc-stat-grid">
          {stats.map((stat, index) => (
            <Link className="tc-stat" to={stat.to} key={`${stat.label}-${index}`}>
              <span className="tc-stat-label">{stat.label}</span>
              <strong>{stat.value}</strong>
              <span className="tc-stat-note">{stat.note}</span>
            </Link>
          ))}
        </div>
      </section>
    </div>
  )
}

function HomePrimaryAction({
  isSiteAdmin,
  isSchoolAdmin,
  isTeacher,
  isReviewer,
  isPrinter,
}: {
  isSiteAdmin: boolean
  isSchoolAdmin: boolean
  isTeacher: boolean
  isReviewer: boolean
  isPrinter: boolean
}) {
  const destination = isSiteAdmin
    ? ['/schools', 'Manage schools']
    : isSchoolAdmin
      ? ['/settings', 'School settings']
      : isTeacher
        ? ['/papers', 'View papers']
        : isReviewer
          ? ['/review', 'Open review']
          : isPrinter
            ? ['/printing', 'Open printing']
            : null
  if (!destination) return null
  return <Link className="tc-btn tc-btn-primary" to={destination[0]}>{destination[1]}</Link>
}

/** What a school loses when nobody holds the role, in a teacher's words. */
const ROLE_GAP: Record<string, { noun: string; verb: string }> = {
  teacher: { noun: 'A teacher', verb: 'write papers' },
  reviewer: { noun: 'A reviewer', verb: 'check papers' },
}

function listRoles(items: string[]): string {
  if (items.length <= 1) return items[0] ?? ''
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1].toLowerCase()}`
}

function greeting(hour = new Date().getHours()) {
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}

function roleMessage(roles: string[]) {
  if (roles.includes('admin')) return 'Platform overview'
  if (roles.includes('school_admin')) return 'School overview'
  if (roles.includes('print_admin')) return 'Print room overview'
  if (roles.includes('reviewer')) return 'Review overview'
  return 'Teaching overview'
}

function welcomeMessage(roles: string[]) {
  if (roles.includes('admin')) {
    // Says what this person actually does. It used to promise "every paper,
    // review, and print queue", which was both true and wrong: schools' work is
    // theirs, not the platform operator's.
    return 'Onboard schools, hand them to their administrators, and keep the platform running.'
  }
  if (roles.includes('school_admin')) {
    return 'Your people, paper headers, and printing work are all within reach.'
  }
  if (roles.includes('print_admin')) return 'See what is ready and keep the print queue moving.'
  if (roles.includes('reviewer')) return 'See what needs checking and where comments remain open.'
  return 'Pick up a paper or start something new.'
}
